'use client';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';

import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import { CTACornerButton } from '@/shadcn/ui/corner-button';

import { PageCareerSchema } from '@/sanity/pages/PageCareerTemplate';

// how long each hero image stays on screen before the slider advances
const SLIDE_DURATION = 6000;

export default function HeroSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const images = data?.heroImages?.filter((image) => image?.src) ?? [];
  const [current, setCurrent] = useState(0);

  // a single image is a static hero — the indicator and the timer only exist once there is something to cycle through
  useEffect(() => {
    if (images.length < 2) return;

    const timer = setInterval(
      () => setCurrent((index) => (index + 1) % images.length),
      SLIDE_DURATION,
    );
    return () => clearInterval(timer);
  }, [images.length, current]);

  return (
    <section
      className={cn(
        'relative overflow-clip pt-[9rem] lg:pt-[15rem]',
        className,
      )}
    >
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="relative mx-auto flex w-[min(187.875rem,100%)] flex-col items-center gap-[3rem] px-[3rem] md:px-[5rem] lg:gap-[4.5rem] lg:px-0 pb-[4rem]">
        <div className="flex flex-col items-center gap-[2.5rem]">
          <div className="flex flex-col items-center gap-[1.75rem]">
            {data?.heroChip?.text && (
              <Chip
                className="mb-0 [&_img]:!w-[3rem] [&_p]:font-geistmono [&_p]:uppercase [&_p]:text-[#202223]"
                image={data?.heroChip?.icon}
                text={data?.heroChip?.text}
              />
            )}

            {data?.heroTitle && (
              /*
                Heading + paragraph live in the same richtext field: the "new" theme
                already renders h2 as Funnel 7rem and p as Geist 2.25rem/60%, so only
                the serif italic run and the paragraph width need spelling out here.
              */
              <div className="pixel-highlight richtext richtext-center prose-headings:mb-[2rem] prose-em:font-ivy prose-em:!px-0 lg:prose-headings:mb-[3rem] lg:prose-em:!px-[.7rem] prose-p:w-[min(86.375rem,100%)]">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.heroCTA && <CTACornerButton data={data.heroCTA} />}
        </div>

        {images.length > 0 && (
          /*
            The top-right corner is knocked out at 45°, matching the plate the design
            lays over it — the plate scales with the frame, so the cut goes 21px on
            mobile against 44px on desktop. The crop turns 16:9 there as well.
          */
          <div className="relative aspect-[16/9] w-full overflow-clip [clip-path:polygon(0_0,calc(100%-2.625rem)_0,100%_2.625rem,100%_100%,0_100%)] lg:aspect-[1503/530] lg:[clip-path:polygon(0_0,calc(100%-5.5rem)_0,100%_5.5rem,100%_100%,0_100%)]">
            {images.map((image, index) => (
              <Image
                key={index}
                className={cn(
                  'absolute left-0 top-0 !h-full !w-full object-cover !object-top transition-opacity duration-700',
                  index === current ? 'opacity-100' : 'opacity-0',
                )}
                src={image}
                alt=""
                priority={index === 0}
              />
            ))}

            <div className="pointer-events-none absolute inset-0 bg-black/10" />

            {images.length > 1 && (
              /* the whole indicator is the desktop one at 0.72 scale, as drawn */
              <div className="absolute bottom-[0.875rem] left-1/2 -translate-x-1/2 rounded-full border border-white/[.22] p-[0.375rem] lg:bottom-[2.375rem] lg:p-[0.5rem]">
                <div className="flex h-[3.375rem] items-center gap-[0.375rem] rounded-full bg-[#F3F2F1] px-[1.5rem] lg:h-[4.625rem] lg:gap-[0.5rem] lg:px-[2.125rem]">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrent(index)}
                      aria-label={`Bild ${index + 1}`}
                      aria-current={index === current}
                      className={cn(
                        'h-[0.75rem] rounded-full transition-all duration-300 lg:h-[1.125rem]',
                        index === current
                          ? 'w-[3.5rem] bg-[#202223] lg:w-[4.875rem]'
                          : 'w-[0.75rem] bg-[#D9D7D5] hover:bg-[#202223]/40 lg:w-[1.125rem]',
                      )}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
